import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Loader2, Check, Sparkles, Crosshair } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { SENDAFooter } from '../components/SENDAFooter';
import { ModuleFeedback } from '../components/ModuleFeedback';
import { ReadOnlyBanner } from '../components/ReadOnlyBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Modulo73PuntoIntervencion() {
    const [patron, setPatron] = useState('');
    const [tipoIntervencion, setTipoIntervencion] = useState('');
    const [accionEspecifica, setAccionEspecifica] = useState('');
    const [loading, setLoading] = useState(false);
    const [savingDraft, setSavingDraft] = useState(false);
    const [loadingDraft, setLoadingDraft] = useState(true);
    const [readOnly, setReadOnly] = useState(false);
    const [submittedAt, setSubmittedAt] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [aiInsight, setAiInsight] = useState('');
    const [impacto, setImpacto] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/user/draft/punto_intervencion`, { withCredentials: true });
                if (data.data) {
                    if (data.data.patron) setPatron(data.data.patron);
                    if (data.data.tipoIntervencion) setTipoIntervencion(data.data.tipoIntervencion);
                    if (data.data.accionEspecifica) setAccionEspecifica(data.data.accionEspecifica);
                }
                if (data.read_only) { setReadOnly(true); setSubmittedAt(data.submitted_at); }
            } catch {} finally { setLoadingDraft(false); }
        })();
    }, []);

    const saveDraft = useCallback(async () => {
        setSavingDraft(true);
        try { await axios.post(`${API_URL}/api/user/draft/save`, { module_id: 'punto_intervencion', data: { patron, tipoIntervencion, accionEspecifica } }, { withCredentials: true }); } catch {} finally { setSavingDraft(false); }
    }, [patron, tipoIntervencion, accionEspecifica]);

    useEffect(() => { if (loadingDraft || readOnly) return; const t = setTimeout(saveDraft, 2000); return () => clearTimeout(t); }, [patron, tipoIntervencion, accionEspecifica, saveDraft, loadingDraft, readOnly]);

    const canSubmit = !readOnly && (patron.trim().length > 0 && tipoIntervencion && accionEspecifica.trim().length > 0);

    const handleSubmit = async () => {
        if (readOnly) return;
        if (!canSubmit) return;
        setLoading(true);
        try {
            const { data } = await axios.post(`${API_URL}/api/user/modules/punto-intervencion/submit`, {
                patron, tipo_intervencion: tipoIntervencion, accion_especifica: accionEspecifica
            }, { withCredentials: true });
            setAiInsight(data.ai_insight);
            setImpacto(data.impacto);
            setSubmitted(true);
            toast.success('Punto de intervención definido');
        } catch (error) { toast.error(error.response?.data?.detail || 'Error al enviar'); }
        finally { setLoading(false); }
    };

    if (loadingDraft) return <div className="min-h-screen bg-[#F5F7F8] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#3E5F6E]" /></div>;

    if (submitted) return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <div className="flex-1 flex items-center justify-center p-4">
                <Card className="w-full max-w-xl bg-white border border-[#DCE3E8] rounded-2xl shadow-xl"><CardContent className="pt-8 text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#3E5F6E] to-[#2F4A57] rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg"><Crosshair className="h-10 w-10 text-white" /></div>
                    <h2 className="text-2xl font-light text-[#1F2A30] font-['Outfit'] mb-2" data-testid="punto-intervencion-success-title">Punto de intervención definido</h2>
                    <p className="text-[#3E5F6E] font-medium mb-4">Tienes un punto preciso de acción</p>
                    {impacto && <div className="inline-block px-4 py-1.5 bg-[#5FAF9D]/10 text-[#5FAF9D] rounded-full text-sm font-medium mb-4">Impacto esperado: {impacto}</div>}
                    {aiInsight && <div className="bg-[#F5F7F8] border-l-4 border-[#3E5F6E] p-4 rounded-r-xl text-left mb-6"><div className="flex items-start gap-3"><Sparkles className="h-5 w-5 text-[#3E5F6E] shrink-0 mt-0.5" /><p className="text-[#1F2A30] italic text-sm">"{aiInsight}"</p></div></div>}
                    <div className="mb-6"><ModuleFeedback moduleId="punto_intervencion" chapter={7} /></div>
                    <Link to="/usuario"><Button className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-8 py-3 rounded-xl" data-testid="continue-after-punto-intervencion">Continuar</Button></Link>
                </CardContent></Card>
            </div><SENDAFooter /></div>
    );

    return (
        <div className="min-h-screen bg-[#F5F7F8] flex flex-col"><Toaster position="top-right" richColors />
            <header className="bg-white/90 backdrop-blur-xl border-b border-[#DCE3E8]/50 sticky top-0 z-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3 sm:gap-4">
                            <Link to="/usuario"><Button variant="ghost" size="icon" className="text-[#3E5F6E]"><ArrowLeft className="h-5 w-5" /></Button></Link>
                            <div>
                                <div className="flex items-center gap-2"><span className="text-xs font-semibold text-[#7A9BA8] uppercase tracking-wide">Cap. 7</span><span className="text-xs text-[#7A8A93]">7.3</span></div>
                                <h1 className="text-base sm:text-lg font-semibold text-[#1F2A30] font-['Outfit']">Punto de intervención</h1>
                            </div>
                        </div>
                        {savingDraft && <Loader2 className="h-3 w-3 animate-spin text-[#7A8A93]" />}
                    </div>
                </div>
            </header>
            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 flex-1">
                                {readOnly && <ReadOnlyBanner submittedAt={submittedAt} />}
                <Card className="bg-white border border-[#DCE3E8] rounded-2xl sm:rounded-3xl shadow-sm overflow-hidden">
                    <CardHeader className="bg-gradient-to-r from-[#3E5F6E] to-[#2F4A57] p-4 sm:p-6 text-white">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center"><Crosshair className="h-5 w-5 sm:h-6 sm:w-6 text-white" /></div>
                            <div><CardTitle className="text-white text-base sm:text-lg">Elige tu punto de palanca</CardTitle><p className="text-xs sm:text-sm text-white/70">Intervención simple con máximo impacto</p></div>
                        </div>
                    </CardHeader>
                    <CardContent className="p-4 sm:p-6 space-y-5">
                        <div className="space-y-2">
                            <Label className="text-sm font-semibold text-[#3E5F6E]">Patrón a intervenir</Label>
                            <p className="text-xs text-[#7A8A93]">Describe brevemente el patrón que quieres cambiar</p>
                            <Textarea value={patron} onChange={e => setPatron(e.target.value)} placeholder="Ej: Cuando recibo un correo exigente, cierro todo y pospongo..." className="border-[#DCE3E8] rounded-xl min-h-[60px]" data-testid="input-patron-interv" />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-sm font-semibold text-[#3E5F6E]">Tipo de intervención</Label>
                            <Select defaultValue={tipoIntervencion || undefined} onValueChange={setTipoIntervencion}>
                                <SelectTrigger className="border-[#DCE3E8] rounded-xl" data-testid="select-tipo-intervencion"><SelectValue placeholder="¿Dónde vas a intervenir?" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="antes_disparador">Antes del disparador: prevención</SelectItem>
                                    <SelectItem value="en_disparador">En el disparador: sustitución</SelectItem>
                                    <SelectItem value="en_respuesta">En la respuesta: cambio de acción</SelectItem>
                                    <SelectItem value="despues_respuesta">Después: corrección rápida</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2 p-4 bg-[#5FAF9D]/5 rounded-xl border border-[#5FAF9D]/20">
                            <Label className="text-sm font-semibold text-[#5FAF9D]">Acción específica</Label>
                            <p className="text-xs text-[#7A8A93]">¿Qué harás exactamente en ese punto?</p>
                            <Textarea value={accionEspecifica} onChange={e => setAccionEspecifica(e.target.value)} placeholder="Ej: Al ver el correo, respiraré 3 veces y responderé solo con la primera frase..." className="border-[#5FAF9D]/20 bg-white rounded-xl min-h-[70px]" data-testid="input-accion-especifica" />
                        </div>
                        <div className="flex justify-end pt-4 border-t border-[#DCE3E8]">
                            <Button onClick={handleSubmit} disabled={!canSubmit || loading} className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white px-6 sm:px-8 py-3 rounded-xl shadow-md" data-testid="submit-punto-intervencion">
                                {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Procesando...</> : <><Check className="h-4 w-4 mr-2" />Definir intervención</>}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            </main>
            <SENDAFooter />
        </div>
    );
}
